import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, History, Search } from "lucide-react";
import { Fragment, useState } from "react";

import { getAuditoria, getAuditoriaCatalogo, type AuditoriaItem } from "@/lib/api";

/*
 * Configuración → Auditoría: bitácora transversal (mig 126). Los triggers de
 * etrm.fn_auditoria registran quién cambió qué en las tablas maestras:
 * contratos y parámetros TBFin, contrapartes, matriz RBAC, usuarios,
 * configuración (tipo de cambio…), tarifas, nodos Cauchy, posiciones,
 * garantías, facturas, oficios, grupos B2B, portafolio.
 */

const OP_LABEL: Record<AuditoriaItem["operacion"], { texto: string; cls: string }> = {
  INSERT: { texto: "Alta", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  UPDATE: { texto: "Cambio", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  DELETE: { texto: "Baja", cls: "bg-red-50 text-red-700 border-red-200" },
};

// Columnas que no aportan al leer una fila completa.
const OCULTAS = new Set(["creado_en", "actualizado_en", "guardado_en", "registrado_en", "subido_en"]);

function fmtValor(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "sí" : "no";
  if (typeof v === "number") return v.toLocaleString("es-MX", { maximumFractionDigits: 4 });
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      return new Date(v).toLocaleString("es-MX", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    }
    return v.length > 160 ? v.slice(0, 157) + "…" : v;
  }
  const s = JSON.stringify(v);
  return s.length > 160 ? s.slice(0, 157) + "…" : s;
}

function fmtTs(ts: string): string {
  return new Date(ts).toLocaleString("es-MX", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

const hoyIso = () => new Date().toISOString().slice(0, 10);
const haceDias = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

export function AuditoriaPage() {
  const [tabla, setTabla] = useState("");
  const [usuario, setUsuario] = useState("");
  const [operacion, setOperacion] = useState("");
  const [desde, setDesde] = useState(haceDias(30));
  const [hasta, setHasta] = useState(hoyIso());
  const [q, setQ] = useState("");
  const [qAplicada, setQAplicada] = useState("");
  const [abiertos, setAbiertos] = useState<Set<number>>(new Set());
  // Paginación hacia atrás: acumula páginas con `antes_de`.
  const [paginas, setPaginas] = useState<AuditoriaItem[][]>([]);
  const [antesDe, setAntesDe] = useState<number | undefined>(undefined);

  const filtros = { tabla, usuario, operacion, desde, hasta, q: qAplicada, limite: 200, antes_de: antesDe };
  const cat = useQuery({ queryKey: ["auditoria-catalogo"], queryFn: getAuditoriaCatalogo, staleTime: 60_000 });
  const lista = useQuery({
    queryKey: ["auditoria", filtros],
    queryFn: () => getAuditoria(filtros),
    placeholderData: (prev) => prev,
  });

  const reiniciar = () => {
    setPaginas([]);
    setAntesDe(undefined);
    setAbiertos(new Set());
  };
  const items = [...paginas.flat(), ...(lista.data?.items ?? [])];

  const toggle = (id: number) =>
    setAbiertos((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <History className="w-5 h-5 text-slate-500" /> Auditoría
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Quién cambió qué y cuándo en contratos, parámetros TBFin, contrapartes, matriz de
          permisos, usuarios, configuración, tarifas, nodos Cauchy, posiciones de mercado,
          garantías, facturas, oficios, grupos B2B y portafolio. En los cambios solo se muestran
          las columnas que cambiaron.
        </p>
      </div>

      <form
        className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          setQAplicada(q.trim());
          reiniciar();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Tabla</span>
          <select
            value={tabla}
            onChange={(e) => { setTabla(e.target.value); reiniciar(); }}
            className="border rounded px-2 py-1.5 min-w-[220px]"
          >
            <option value="">Todas</option>
            {cat.data?.tablas.map((t) => (
              <option key={t.tabla} value={t.tabla}>{t.label} ({t.n})</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Usuario</span>
          <input
            list="auditoria-usuarios"
            value={usuario}
            onChange={(e) => { setUsuario(e.target.value); reiniciar(); }}
            placeholder="correo…"
            className="border rounded px-2 py-1.5 min-w-[200px]"
          />
          <datalist id="auditoria-usuarios">
            {cat.data?.usuarios.map((u) => <option key={u.usuario} value={u.usuario} />)}
          </datalist>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Operación</span>
          <select
            value={operacion}
            onChange={(e) => { setOperacion(e.target.value); reiniciar(); }}
            className="border rounded px-2 py-1.5"
          >
            <option value="">Todas</option>
            <option value="INSERT">Alta</option>
            <option value="UPDATE">Cambio</option>
            <option value="DELETE">Baja</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Desde</span>
          <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); reiniciar(); }}
            className="border rounded px-2 py-1.5" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Hasta</span>
          <input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); reiniciar(); }}
            className="border rounded px-2 py-1.5" />
        </label>
        <label className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <span className="text-xs text-slate-500">Texto (clave o contenido)</span>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="folio, nombre, valor…"
              className="border rounded pl-8 pr-2 py-1.5 w-full"
            />
          </div>
        </label>
        <button type="submit" className="bg-blue-600 text-white rounded px-4 py-1.5 hover:bg-blue-700">
          Buscar
        </button>
      </form>

      {lista.isError && <div className="text-sm text-red-600">{(lista.error as Error).message}</div>}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm" data-hoja="Auditoría">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left w-6"></th>
              <th className="px-3 py-2 text-left">Cuándo</th>
              <th className="px-3 py-2 text-left">Usuario</th>
              <th className="px-3 py-2 text-left">Tabla</th>
              <th className="px-3 py-2 text-left">Operación</th>
              <th className="px-3 py-2 text-left">Clave</th>
              <th className="px-3 py-2 text-left">Qué cambió</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !lista.isLoading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                Sin registros con esos filtros.
              </td></tr>
            )}
            {items.map((it) => {
              const op = OP_LABEL[it.operacion];
              const abierto = abiertos.has(it.id);
              const cambios = it.cambios ? Object.entries(it.cambios) : [];
              const fila = it.fila ? Object.entries(it.fila).filter(([k]) => !OCULTAS.has(k)) : [];
              return (
                <Fragment key={it.id}>
                  <tr className="border-t hover:bg-slate-50 align-top cursor-pointer" onClick={() => toggle(it.id)}>
                    <td className="px-3 py-2 text-slate-400">
                      {abierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">{fmtTs(it.ts)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{it.usuario ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{it.tabla_label}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-block border rounded-full px-2 py-0.5 text-xs ${op.cls}`}>{op.texto}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{it.clave}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {it.operacion === "UPDATE" ? (
                        <ul className="space-y-0.5">
                          {cambios.slice(0, abierto ? undefined : 3).map(([col, c]) => (
                            <li key={col}>
                              <span className="font-medium text-slate-800">{col}</span>:{" "}
                              <span className="text-red-700 line-through decoration-red-300">{fmtValor(c.antes)}</span>
                              {" → "}
                              <span className="text-emerald-700">{fmtValor(c.despues)}</span>
                            </li>
                          ))}
                          {!abierto && cambios.length > 3 && (
                            <li className="text-xs text-slate-400">… y {cambios.length - 3} más</li>
                          )}
                        </ul>
                      ) : (
                        <span className="text-slate-500">
                          {fila.slice(0, 3).map(([k, v]) => `${k}: ${fmtValor(v)}`).join(" · ")}
                          {fila.length > 3 && !abierto ? " · …" : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                  {abierto && it.operacion !== "UPDATE" && (
                    <tr className="border-t bg-slate-50/60">
                      <td></td>
                      <td colSpan={6} className="px-3 py-2">
                        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 text-xs">
                          {fila.map(([k, v]) => (
                            <Fragment key={k}>
                              <dt className="font-medium text-slate-700">{k}</dt>
                              <dd className="text-slate-600 break-all">{fmtValor(v)}</dd>
                            </Fragment>
                          ))}
                        </dl>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <span>{items.length} registros{lista.isFetching ? " · cargando…" : ""}</span>
        {lista.data?.hay_mas && (
          <button
            type="button"
            className="border rounded px-3 py-1 hover:bg-slate-50"
            onClick={() => {
              const ult = lista.data!.items[lista.data!.items.length - 1];
              setPaginas((p) => [...p, lista.data!.items]);
              setAntesDe(ult.id);
            }}
          >
            Cargar más antiguos
          </button>
        )}
      </div>
    </div>
  );
}
